import React, { useEffect, useMemo, useState } from 'react';
import { Accordion, Button } from '@saleshandy/design-system';
import { ChevronDown, ChevronUp, User, Users } from '@saleshandy/icons';
import { Placement } from '../../design-system/components/overlay';
import Checkbox from '../../design-system/components/atoms/checkbox';
import type { IProps } from './teams-filter-container';
import { TeamForFilter } from './types';
import { accessibleOnClick } from '../../utils/accessible-on-click';
import { getSelectedOptionKey } from './utils/get-selected-option-keys';
import { getOptions } from './utils/get-options';
import { getFilterOptions } from './utils/get-filtered-options';
import {
  onUserOptionClicked,
  onTeamOptionClicked,
} from './utils/on-select-deselect';
import {
  getIsAllOptionsSelected,
  getIsSelectAllCheckboxChecked,
} from './utils/get-is-all-options-selected';
import Transfer from '../../design-system/components/transfer';
import {
  executeOnErrorWithErrorCheck,
  executeOnRequestStatus,
  getIsRequestPending,
  getUniqueNumbers,
} from '../../utils';
import toaster from '../../toaster';
import TruncatedTextWithTooltip from '../truncated-text-with-tooltip';
import { Images } from '../../app-constants';

const TeamsFilter: React.FC<IProps> = ({
  selectLabel = 'team',
  btnClassName = '',
  teamsAndUsersList: teamsAndUsersListData,
  data = [],
  resetSelectedUserIds = false,
  setResetSelectedUserIds = null,
  onUserIdsChange,
  defaultSelectedUsers,
  shouldMakeInitialAPICall = true,

  customSelectBox = null,
  hideFooter = false,
  transferClassName = '',
  disabled = false,

  sendGetTeamsAndMembersListRequest,
  resetGetTeamsAndMembersListRequest,
  getTeamsAndMembersListRequestStatus,
  getTeamsAndMembersListRequestError,
}) => {
  const [show, setShow] = useState(false);
  const [isEmpty, setIsEmpty] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [isFilterDirty, setIsFilterDirty] = useState(false);
  const [search, setSearch] = useState('');

  const teamsAndUsersList = useMemo(() => {
    if (teamsAndUsersListData.length > 0) {
      return teamsAndUsersListData;
    }

    if (data?.length > 0) {
      return data;
    }

    return [];
  }, [teamsAndUsersListData, data]);

  const options: TeamForFilter[] = useMemo(
    () => getOptions(teamsAndUsersList),
    [teamsAndUsersList],
  );

  const isAllOptionsSelected = getIsAllOptionsSelected(
    options,
    selectedUserIds,
  );
  const {
    isSelectAllCheckboxChecked,
    intermediate,
  } = getIsSelectAllCheckboxChecked(options, selectedUserIds, search);

  const onSelectDeselect = (option: TeamForFilter) => {
    if (option.type === 'user') {
      onUserOptionClicked(option.id, selectedUserIds, setSelectedUserIds);
    }
    if (option.type === 'team') {
      onTeamOptionClicked(option, selectedUserIds, setSelectedUserIds);
    }
    if (!isFilterDirty) {
      setIsFilterDirty(true);
    }
  };

  const onSelectAll = (checked: boolean) => {
    const filteredOptions = getFilterOptions(
      getOptions(teamsAndUsersList),
      search,
    );

    const allUserIds = filteredOptions.reduce((userIds, team) => {
      if (team.type === 'team') {
        userIds.push(...team.users.map((user) => user.id));
      } else if (team.type === 'user') {
        userIds.push(team.id);
      }
      return userIds;
    }, []);

    if (checked) {
      setSelectedUserIds(getUniqueNumbers([...selectedUserIds, ...allUserIds]));
    } else {
      setSelectedUserIds(
        selectedUserIds.filter((id) => !allUserIds.includes(id)),
      );
    }
    if (!isFilterDirty) {
      setIsFilterDirty(true);
    }
  };

  const getLabelAndCount = (userIds: number[]) => {
    let label = '';
    let count = 14;

    if (isAllOptionsSelected) {
      label = selectLabel === 'owners' ? 'Sequence Owner' : 'All Teams';
      count = 20;
    } else {
      label =
        selectLabel === 'owners'
          ? `${userIds.length} Sequence Owner selected`
          : `${userIds.length} Team Members selected`;
      count = selectLabel === 'owners' ? 13 : 10;
    }

    return { label, count };
  };

  useEffect(() => {
    if (shouldMakeInitialAPICall) {
      sendGetTeamsAndMembersListRequest();
    }
  }, [shouldMakeInitialAPICall]);

  useEffect(() => {
    if (defaultSelectedUsers?.length) {
      setSelectedUserIds(defaultSelectedUsers);
    }
  }, [defaultSelectedUsers, show]);

  useEffect(() => {
    if (resetSelectedUserIds) {
      setSelectedUserIds([]);
      setResetSelectedUserIds?.(false);
    }
  }, [resetSelectedUserIds]);

  useEffect(() => {
    if (hideFooter && isFilterDirty) {
      onUserIdsChange(selectedUserIds);
    }
  }, [selectedUserIds]);

  useEffect(() => {
    executeOnRequestStatus({
      status: getTeamsAndMembersListRequestStatus,
      onSuccess: () => {
        resetGetTeamsAndMembersListRequest();
      },
      onFailed: () => {
        executeOnErrorWithErrorCheck({
          error: getTeamsAndMembersListRequestError,
          onError: () => {
            toaster.error(getTeamsAndMembersListRequestError.message);
          },
        });
        resetGetTeamsAndMembersListRequest();
      },
    });
  }, [getTeamsAndMembersListRequestStatus]);

  useEffect(() => {
    if (!show && isFilterDirty) {
      setIsFilterDirty(false);
    }
  }, [show]);

  return (
    <Transfer<TeamForFilter>
      show={show}
      onToggle={(value) => {
        if (!disabled) {
          setShow(value);
        }
      }}
      showSearch
      className={`team-filter-select ${transferClassName}`}
      multiSelect
      placement={Placement.BottomStart}
      options={options}
      selectedOptionKey={getSelectedOptionKey(
        teamsAndUsersList,
        selectedUserIds,
        search,
      )}
      optionFilterProp="name"
      searchPlaceholder="Search"
      onSearch={(value) => {
        setSearch(value);
      }}
      customFilterOption={(_filteredOptions, searchValue) => {
        const filteredOptions = getFilterOptions(
          getOptions(teamsAndUsersList),
          searchValue,
        );

        if (filteredOptions.length === 0 && !isEmpty) {
          setIsEmpty(true);
        } else if (filteredOptions.length !== 0 && isEmpty) {
          setIsEmpty(false);
        }

        return filteredOptions;
      }}
      onSelect={onSelectDeselect}
      onDeselect={onSelectDeselect}
      optionRenderer={(option: TeamForFilter) =>
        option.type === 'team' ? (
          <Accordion
            allowMultipleExpanded
            allowZeroExpanded
            preExpanded={[option.id]}
          >
            <Accordion.Item uuid={option.id}>
              <Accordion.ItemHeading>
                <Accordion.ItemButton>
                  <span>{option.name}</span>
                  <Accordion.ItemState>
                    {({ expanded }) =>
                      expanded ? <ChevronUp /> : <ChevronDown />
                    }
                  </Accordion.ItemState>
                </Accordion.ItemButton>
              </Accordion.ItemHeading>
              {option.users?.length > 0 && (
                <Accordion.ItemPanel>
                  {option.users.map((user) => (
                    <div
                      className="team-member"
                      {...accessibleOnClick(() => {
                        onUserOptionClicked(
                          user.id,
                          selectedUserIds,
                          setSelectedUserIds,
                        );
                        if (!isFilterDirty) {
                          setIsFilterDirty(true);
                        }
                      })}
                    >
                      <Checkbox checked={selectedUserIds.includes(user.id)} />
                      <span className="team-member-name">
                        {user.userName.trim() || user.email}
                      </span>
                    </div>
                  ))}
                </Accordion.ItemPanel>
              )}
            </Accordion.Item>
          </Accordion>
        ) : (
          <div
            className="single-team-member"
            {...accessibleOnClick(() => {
              onUserOptionClicked(
                option.id,
                selectedUserIds,
                setSelectedUserIds,
              );
              if (!isFilterDirty) {
                setIsFilterDirty(true);
              }
            })}
          >
            <span className="team-member-name">
              {option.name.trim() || option.email}
            </span>
          </div>
        )
      }
      isLoading={getIsRequestPending(getTeamsAndMembersListRequestStatus)}
      showOptionsSeparator={true}
      clickListenerOn="checkbox"
      {...(!isEmpty &&
        !hideFooter && {
          footer: (
            <div className="team-filter-footer d-flex justify-content-between align-items-center">
              <Checkbox
                checked={isSelectAllCheckboxChecked}
                intermediate={intermediate}
                label="Select All"
                onChange={onSelectAll}
              />
              <Button
                onClick={() => {
                  onUserIdsChange(selectedUserIds);
                  setShow(false);
                  setSearch('');
                }}
                disabled={selectedUserIds.length === 0 || !isFilterDirty}
                className="apply-btn"
              >
                Apply
              </Button>
            </div>
          ),
        })}
      customEmptyElement={
        <>
          <img src={Images.NoResult} alt="No Result" />
          <h1>No Result Found</h1>
          <p>Please try searching with another term</p>
        </>
      }
    >
      <Button
        className={`transfer-select-btn ${btnClassName} ${
          disabled ? 'disabled' : ''
        }`}
        variant="tertiary"
        onClick={() => {
          if (!disabled) {
            setShow(!show);
          }
        }}
      >
        <div className="transfer-select-btn-content">
          {customSelectBox || (
            <>
              <div className="select-icon">
                {selectLabel === 'team' ? <Users /> : <User />}
              </div>
              <TruncatedTextWithTooltip
                string={getLabelAndCount(selectedUserIds).label}
                length={getLabelAndCount(selectedUserIds).count}
              />
            </>
          )}
        </div>
        <div className="bs-select-box-icon-wrapper">
          {show ? <ChevronUp /> : <ChevronDown />}
        </div>
      </Button>
    </Transfer>
  );
};

export default TeamsFilter;
